/* Account list: every Account in view with its status, its numbers for the period
   and the AI switch (when the person may change it). List or Tiles; the body
   scrolls (only the rows on screen are drawn). Clicking one opens the Account, as
   picking it in the sidebar does. The ⋯ menu turns the AI on or off for all. */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Account } from '../../api/client';
import { AccountAvatar } from '../../components/AccountAvatar';
import { C } from '../../components/ChatAssistant/compact/tokens';
import { font as f } from '../../components/ChatAssistant/compact/style';
import { Icon, Toggle } from '../../components/ChatAssistant/compact/kit';
import { ConfirmDialog, type ConfirmSpec } from '../../components/Payments/ui';
import { toastQueue } from '../../components/ToastProvider';
import { fmt, pick, usd } from '../../components/Statistics/unified/model';
import { accountName, accountStatus, attentionItems, type AccountStatus, type AttentionItem } from '../attention';
import { periodRange, useDash, useSource } from '../context';
import type { AccountCol, WidgetOf } from '../model';
import { BodyNote, WidgetFrame, type FrameProps } from '../WidgetFrame';
import { bodySize } from '../frameUtils';
import { aiShare, useMetaText } from './shared';

const STATUS_LOOK: Record<AccountStatus, { icon: string; color: string }> = {
  killed: { icon: 'octagon', color: C.purplePale },
  signed_out: { icon: 'log-out', color: C.purplePale },
  billing_suspended: { icon: 'credit-card', color: C.purplePale },
  throttled: { icon: 'alert-triangle', color: C.purplePale },
  live: { icon: 'zap', color: C.purpleLt },
  pending: { icon: 'loader', color: C.muted },
  off: { icon: 'pause-circle', color: C.muted },
};

const ROW_H = 38;
const TILE_H = 112;
const TILE_GAP = 10;
const COL_W: Record<AccountCol, number> = { status: 104, rev: 56, ai: 52, chats: 62 };
/** Row padding + avatar + the least the name may shrink to. */
const ROW_FIXED = 16 + 16 + 34 + 70;
const TOGGLE_W = 38;

interface Line {
  account: Account;
  name: string;
  status: AccountStatus;
  issues: AttentionItem[];
  rank: number;
  revenue: number | null;
  ai: number | null;
  chats: number | null;
}

/** Draws only the rows in view: fleets run to hundreds of Accounts. */
function Virtual({ count, rowH, height, children, pad = 0 }: { count: number; rowH: number; height: number; pad?: number; children: (i: number) => React.ReactNode }) {
  const [top, setTop] = useState(0);
  const first = Math.max(0, Math.floor((top - pad) / rowH) - 4);
  const last = Math.min(count, Math.ceil((top + height) / rowH) + 4);
  return (
    <div className="dbw-scroll" onScroll={(e) => setTop(e.currentTarget.scrollTop)} style={{ flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative' }}>
      <div style={{ height: count * rowH + pad * 2, position: 'relative' }}>
        {Array.from({ length: Math.max(0, last - first) }, (_, k) => {
          const i = first + k;
          return <div key={i} style={{ position: 'absolute', top: pad + i * rowH, left: 0, right: 0, height: rowH }}>{children(i)}</div>;
        })}
      </div>
    </div>
  );
}

export function AccountList(p: FrameProps & { widget: WidgetOf<'accounts'> }) {
  const { t } = useTranslation('dashboardWidgets');
  const { t: td } = useTranslation('dashboard');
  const env = useDash();
  const { accounts, subs, throttled, money, today, access, hasStatsOn, canToggle, openAccount, setEnabled } = env;
  const meta = useMetaText();
  const c = p.widget.cfg;
  const tiles = c.style === 'tiles';
  const cols = c.cols.filter((k) => k === 'status' || access.stats);
  const wantMoney = access.stats && (tiles || cols.includes('rev') || cols.includes('ai') || c.sort === 'rev');
  const wantChats = access.stats && (tiles || cols.includes('chats'));
  const range = periodRange(c.per, today);
  const stats = useSource('stats', wantMoney ? range : null);
  const chat = useSource('chat', wantChats ? range : null);
  const [query, setQuery] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const lines = useMemo(() => {
    const items = attentionItems(accounts, subs, throttled);
    const moneyById = new Map(stats.data?.money.accounts.map((a) => [a.account_id, a]) ?? []);
    const chats = new Map(chat.data?.accounts.map((a) => [a.account_id, a.chats_activated]) ?? []);
    return accounts.map((a): Line => {
      const issues = items.filter((i) => i.account.account_id === a.account_id);
      const m = moneyById.get(a.account_id);
      const sees = hasStatsOn(a.account_id);
      return {
        account: a,
        name: accountName(a),
        status: accountStatus(a, subs[a.account_id], throttled),
        issues,
        rank: issues.some((i) => i.urgent) ? 2 : issues.length ? 1 : 0,
        revenue: sees && stats.data ? pick(m?.revenue, money) : null,
        ai: sees && m ? aiShare(m.sellers, money) : null,
        chats: sees && chat.data ? chats.get(a.account_id) ?? 0 : null,
      };
    });
  }, [accounts, subs, throttled, stats.data, chat.data, money, hasStatsOn]);

  const list = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    const shown = lines.filter((l) => {
      if (c.show === 'att' && !l.issues.length) return false;
      if (c.show === 'off' && l.account.is_enabled) return false;
      return !q || l.name.toLowerCase().includes(q) || (l.account.username ?? '').toLowerCase().includes(q);
    });
    const byName = (a: Line, b: Line) => a.name.localeCompare(b.name);
    const byRev = (a: Line, b: Line) => (b.revenue ?? -1) - (a.revenue ?? -1);
    return shown.sort(c.sort === 'name' ? byName : c.sort === 'rev' ? (a, b) => byRev(a, b) || byName(a, b) : (a, b) => b.rank - a.rank || byRev(a, b) || byName(a, b));
  }, [lines, query, c.show, c.sort]);

  const { width, height } = bodySize(p);
  const anyToggle = accounts.some((a) => canToggle(a.account_id));
  let avail = width - ROW_FIXED - (anyToggle ? TOGGLE_W : 0);
  const visible: AccountCol[] = [];
  for (const k of cols) {
    if (avail < COL_W[k] + 10) break;
    visible.push(k);
    avail -= COL_W[k] + 10;
  }
  const hidden = cols.length - visible.length;
  const tileCols = Math.max(1, Math.floor((width - 24 + TILE_GAP) / 230));

  const toggle = async (a: Account, on: boolean) => {
    setBusy(a.account_id);
    const r = await setEnabled([a], on);
    setBusy(null);
    if (r.failed) toastQueue.add({ title: td('dashboard.toggleFailed', { name: accountName(a) }) });
  };
  const bulk = (on: boolean) => {
    setMenu(false);
    const targets = accounts.filter((a) => canToggle(a.account_id) && a.is_enabled !== on);
    if (!targets.length) {
      toastQueue.add({ title: on ? td('dashboard.bulkAllOnAlready') : td('dashboard.bulkAllOffAlready') });
      return;
    }
    setConfirm({
      title: on ? t('accounts.confirmOnTitle', { count: targets.length }) : t('accounts.confirmOffTitle', { count: targets.length }),
      body: on ? t('accounts.confirmOnBody') : t('accounts.confirmOffBody'),
      confirm: on ? td('dashboard.bulkTurnAllOn') : td('dashboard.bulkTurnAllOff'),
      icon: 'power',
      run: async () => {
        const r = await setEnabled(targets, on);
        toastQueue.add({
          title: r.failed
            ? td('dashboard.bulkPartial', { succeeded: r.ok, failed: r.failed })
            : on ? td('dashboard.bulkTurnedOn', { count: r.ok }) : td('dashboard.bulkTurnedOff', { count: r.ok }),
        });
      },
    });
  };

  // A failed read leaves "—" in the number columns; say so and offer a retry.
  const failed = [stats, chat].filter((x) => x.error);
  const tools = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 'none' }}>
      {failed.length > 0 && (
        <button type="button" className="dbw-icon-btn" title={t('accounts.numbersFailed')} aria-label={t('accounts.numbersFailed')} onClick={() => failed.forEach((x) => x.reload())} style={{ color: C.purplePale }}>
          <Icon name="alert-circle" size={13} />
        </button>
      )}
      {p.widget.w >= 4 && (
        <>
          {query === null ? (
            <button type="button" className="dbw-icon-btn" title={t('accounts.search')} aria-label={t('accounts.search')} onClick={() => setQuery('')} style={{ color: C.sub }}>
              <Icon name="search" size={13} />
            </button>
          ) : (
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setQuery(null); }}
              onBlur={() => { if (!query) setQuery(null); }}
              placeholder={t('accounts.search')}
              aria-label={t('accounts.search')}
              style={{ width: Math.min(160, width / 3), height: 24, padding: '0 8px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(400, 12), outline: 'none' }}
            />
          )}
          {anyToggle && (
            <span style={{ position: 'relative' }}>
              <button type="button" className="dbw-icon-btn" title={t('accounts.more')} aria-label={t('accounts.more')} aria-expanded={menu} onClick={() => setMenu((m) => !m)} style={{ color: C.sub }}>
                <Icon name="more-horizontal" size={14} />
              </button>
              {menu && (
                <>
                  <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                  <div role="menu" className="dbw-menu" style={{ position: 'absolute', top: 30, right: 0, zIndex: 31, width: 180 }}>
                    <button type="button" role="menuitem" className="dbw-menu-row" onClick={() => bulk(true)} style={{ font: f(500, 12.5), color: C.text }}>
                      <Icon name="power" size={13} color={C.purpleLt} />{td('dashboard.bulkTurnAllOn')}
                    </button>
                    <button type="button" role="menuitem" className="dbw-menu-row" onClick={() => bulk(false)} style={{ font: f(500, 12.5), color: C.text }}>
                      <Icon name="power" size={13} color={C.muted} />{td('dashboard.bulkTurnAllOff')}
                    </button>
                  </div>
                </>
              )}
            </span>
          )}
        </>
      )}
    </span>
  );

  const statusLabel = (s: AccountStatus) => t(`accounts.status.${s}`);
  const metaParts = [
    query ? t('accounts.countOf', { n: list.length, m: lines.length }) : t('accounts.count', { count: list.length }),
    !tiles && hidden > 0 && t('accounts.colsHidden', { count: hidden }),
    wantMoney && meta.period(c.per),
  ];
  const aiSwitch = (a: Account) => canToggle(a.account_id) && (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flex: 'none' }}>
      <Toggle on={a.is_enabled} disabled={busy === a.account_id} onChange={(on) => void toggle(a, on)} title={a.is_enabled ? t('accounts.aiOn') : t('accounts.aiOff')} />
    </span>
  );
  const ring = (s: AccountStatus) => (s === 'live' ? C.purple : s === 'off' || s === 'pending' ? C.borderHi : C.purplePale);
  const avatar = (l: Line, size: number, bg: string) => (
    <span style={{ flex: 'none', borderRadius: 99, display: 'flex', boxShadow: `0 0 0 2px ${bg}, 0 0 0 ${size > 24 ? 3.5 : 3}px ${ring(l.status)}` }}>
      <AccountAvatar avatarUrl={l.account.avatar_url} platform={l.account.platform} size={size} />
    </span>
  );

  let body: React.ReactNode;
  let fit: number | undefined = 110;
  if (!accounts.length) body = <BodyNote icon="inbox">{t('accounts.none')}</BodyNote>;
  else if (!list.length) body = <BodyNote>{t('accounts.noMatch')}</BodyNote>;
  else if (tiles) {
    const rows = Math.ceil(list.length / tileCols);
    fit = rows * (TILE_H + TILE_GAP) + 12 * 2;
    body = (
      <Virtual count={rows} rowH={TILE_H + TILE_GAP} height={height} pad={12}>
        {(r) => (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tileCols}, minmax(0, 1fr))`, gap: TILE_GAP, padding: '0 12px', height: TILE_H }}>
            {list.slice(r * tileCols, r * tileCols + tileCols).map((l) => {
              const look = STATUS_LOOK[l.status];
              const issue = l.issues[0];
              return (
                <div key={l.account.account_id} role="button" tabIndex={0} className="dbw-tile" onClick={() => openAccount(l.account)} onKeyDown={(e) => { if (e.key === 'Enter') openAccount(l.account); }}
                  style={{ background: C.surface, border: `1px solid ${issue ? C.tintLine : C.border}`, borderRadius: 9, padding: '11px 12px', display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0, boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    {avatar(l, 26, C.surface)}
                    <span style={{ flex: 1, minWidth: 0, font: f(600, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
                    {aiSwitch(l.account)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, minWidth: 0 }}>
                    <span style={{ flex: 1, font: f(700, 19, 1), color: C.text, whiteSpace: 'nowrap' }}>{l.revenue != null ? usd(l.revenue) : '—'}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, font: f(600, 11), color: look.color, whiteSpace: 'nowrap' }}><Icon name={look.icon} size={11} />{statusLabel(l.status)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, font: f(400, 11.5), color: issue ? C.purplePale : C.sub }}>
                    <Icon name={issue ? 'alert-circle' : 'zap'} size={12} />
                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {issue ? t(`accounts.hint.${issue.kind}`, { count: issue.daysLeft ?? 0 })
                        : t('accounts.tileFoot', { ai: l.ai != null ? `${Math.round(l.ai)}%` : '—', chats: l.chats != null ? fmt(l.chats) : '—' })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Virtual>
    );
  } else {
    fit = list.length * ROW_H + 6 * 2;
    body = (
      <Virtual count={list.length} rowH={ROW_H} height={height} pad={6}>
        {(i) => {
          const l = list[i];
          if (!l) return null;
          const look = STATUS_LOOK[l.status];
          return (
            <div role="button" tabIndex={0} className="dbw-row" onClick={() => openAccount(l.account)} onKeyDown={(e) => { if (e.key === 'Enter') openAccount(l.account); }}
              style={{ height: ROW_H, margin: '0 8px', padding: '0 8px', display: 'flex', alignItems: 'center', gap: 10, boxSizing: 'border-box' }}>
              {avatar(l, 24, C.card)}
              <span style={{ flex: 1, minWidth: 0, font: f(500, 12.5), color: C.body, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
              {visible.map((k) => {
                if (k === 'status') return <span key={k} title={statusLabel(l.status)} style={{ width: COL_W.status, flex: 'none', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', overflow: 'hidden', font: f(500, 11.5), color: look.color }}><Icon name={look.icon} size={12} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{statusLabel(l.status)}</span></span>;
                if (k === 'rev') return <span key={k} style={{ width: COL_W.rev, flex: 'none', textAlign: 'right', font: f(600, 12.5), color: C.text }}>{l.revenue != null ? usd(l.revenue) : '—'}</span>;
                if (k === 'ai') return <span key={k} style={{ width: COL_W.ai, flex: 'none', textAlign: 'right', font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{t('accounts.aiPct', { v: l.ai != null ? `${Math.round(l.ai)}%` : '—' })}</span>;
                return <span key={k} style={{ width: COL_W.chats, flex: 'none', textAlign: 'right', font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{l.chats != null ? t('accounts.chats', { count: l.chats, n: fmt(l.chats) }) : '—'}</span>;
              })}
              {anyToggle && <span style={{ width: TOGGLE_W - 10, flex: 'none', display: 'flex', justifyContent: 'flex-end' }}>{aiSwitch(l.account)}</span>}
            </div>
          );
        }}
      </Virtual>
    );
  }

  return (
    <WidgetFrame {...p} meta={meta.join(metaParts, wantMoney)} tools={tools} fit={fit}>
      {body}
      {confirm && <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />}
    </WidgetFrame>
  );
}
